import { useEffect, useMemo, useRef } from 'react';
import QRCode from 'qrcode';
import { cn } from '../../../lib/utils';
import type { CharlieQrCodeProps } from './index.js';

/**
 * Compute the optimal display size for a QR code based on module count.
 *
 * Each module needs ~3px minimum on screen to be reliably scannable.
 * We round up to the nearest Tailwind size token for consistency.
 *
 * | Modules | Min px | Tailwind |
 * |---------|--------|----------|
 * | ≤ 33    | ~100   | w-48 (192px) — generous for simple codes |
 * | ≤ 57    | ~170   | w-52 (208px) |
 * | ≤ 73    | ~220   | w-56 (224px) |
 * | ≤ 93    | ~280   | w-72 (288px) |
 * | > 93    | ~320   | w-80 (320px) |
 */
function getQrSizeClass(data: string, hasImage: boolean): string {
  try {
    const segments = QRCode.create(data, {
      errorCorrectionLevel: hasImage ? 'H' : 'M',
    });
    const moduleCount = segments.modules.size;

    if (moduleCount <= 33) return 'w-48';
    if (moduleCount <= 57) return 'w-52';
    if (moduleCount <= 73) return 'w-56';
    if (moduleCount <= 93) return 'w-72';
    return 'w-80';
  } catch {
    return 'w-56';
  }
}

/**
 * CharlieQrCode — React equivalent of Rocket's `RocketQrCode`.
 *
 * Renders a QR code onto an HTML `<canvas>` element with optional center-image
 * overlay. Automatically sizes itself based on data complexity to ensure
 * scanability.
 *
 * ## Usage
 *
 * ```tsx
 * import { CharlieQrCode } from '@ver.id/libs.ui.charlie';
 *
 * // Auto-sized based on data complexity
 * <CharlieQrCode data="https://example.com" />
 *
 * // With a center logo (data must be >= 200 chars for the image to render)
 * <CharlieQrCode
 *   data={longPayload}
 *   image="https://example.com/logo.png"
 * />
 *
 * // Override auto-sizing with a custom class
 * <CharlieQrCode data="hello" className="w-64" />
 * ```
 *
 * ## Gotchas
 *
 * - **Center image requires long data**: The `image` prop is only rendered when
 *   `data` is between 200 and 600 characters. Shorter payloads don't have enough
 *   error-correction redundancy to survive the cleared centre region, and very
 *   long payloads produce modules too small for the overlay to look good.
 *
 * - **CORS on images**: The centre image is loaded with `crossOrigin = 'anonymous'`.
 *   Make sure the image server sends appropriate CORS headers, otherwise the
 *   canvas will be tainted and the image won't render.
 */
export function CharlieQrCode({
  data,
  image,
  className,
  testId,
}: CharlieQrCodeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const imageEnabled = !!(image && data && data.length < 600);
  const autoSize = useMemo(
    () => (data ? getQrSizeClass(data, imageEnabled) : 'w-56'),
    [data, imageEnabled],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;

    const scale = data.length < 60 ? 8 : data.length < 120 ? 6 : 4;

    let cancelled = false;

    (async () => {
      try {
        await QRCode.toCanvas(canvas, data, {
          scale,
          margin: 2,
          errorCorrectionLevel: imageEnabled ? 'H' : 'M',
          color: {
            dark: '#000000ff',
            light: '#00000000',
          },
        });

        if (cancelled) return;

        canvas.style.width = '';
        canvas.style.height = '';

        if (imageEnabled) {
          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          const img = await loadImage(image);
          if (cancelled) return;

          const rawImgSize = Math.floor(canvas.width * 0.15);
          const imgSize = Math.round(rawImgSize / scale) * scale;
          const padding = scale;
          const clearSize = imgSize + padding * 2;

          const cx = Math.round((canvas.width - clearSize) / 2 / scale) * scale;
          const cy =
            Math.round((canvas.height - clearSize) / 2 / scale) * scale;

          ctx.clearRect(cx, cy, clearSize, clearSize);

          const finalImageWidth = Math.min(
            imgSize,
            (imgSize * img.width) / img.height,
          );
          const finalImageHeight = Math.min(
            imgSize,
            (imgSize * img.height) / img.width,
          );

          const ix = cx + padding + (imgSize - finalImageWidth) / 2;
          const iy = cy + padding + (imgSize - finalImageHeight) / 2;
          ctx.drawImage(img, ix, iy, finalImageWidth, finalImageHeight);
        }
      } catch (error) {
        console.error('Failed to generate QR code:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data, image, imageEnabled]);

  return (
    <canvas
      ref={canvasRef}
      data-testid={testId}
      className={cn(className ?? autoSize)}
    />
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
